import React from 'react';
import { motion, Variants } from 'framer-motion';
import { Plus, Calendar as CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const floatingBg: Variants = {
    animate: {
        scale: [1, 1.1, 1],
        transition: { duration: 20, repeat: Infinity, ease: 'linear' },
    },
};

const badgeVariants: Variants = {
    animate: {
        opacity: [0.8, 1, 0.8],
        transition: { duration: 2, repeat: Infinity },
    },
};

type Props = {
    onNewAppointment: () => void;
    onViewAgenda: () => void;
};

const HomeHero: React.FC<Props> = ({ onNewAppointment, onViewAgenda }) => {
    const { t } = useTranslation();

    return (
        <div className="relative overflow-hidden rounded-3xl shadow-xl shadow-gray-200/50 group h-full min-h-[340px] z-0 cursor-pointer">
            <div className="absolute inset-0 z-0">
                <motion.img
                    variants={floatingBg}
                    animate="animate"
                    src="https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=1674&q=80"
                    alt="Salon Interior"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/60 md:via-gray-900/40 to-transparent" />
            </div>

            <div className="relative z-10 h-full p-6 md:p-10 flex flex-col justify-center max-w-xl">
                <motion.div
                    variants={badgeVariants}
                    animate="animate"
                    className="bg-white/10 backdrop-blur-md w-fit px-3 py-1 md:px-4 md:py-1.5 rounded-full border border-white/20 mb-4 md:mb-6"
                >
                    <span className="text-white text-[10px] md:text-xs font-bold tracking-widest uppercase flex items-center gap-2">
                        <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-400 rounded-full animate-pulse" />
                        {t('home.active_agenda')}
                    </span>
                </motion.div>

                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                    {t('home.new_appointment')}
                </h2>

                <p className="text-gray-200 mb-6 md:mb-8 text-sm md:text-lg font-light leading-relaxed">
                    {t('home.new_appointment_desc')}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onNewAppointment}
                        className="w-full sm:w-auto px-6 py-3 md:px-8 md:py-3.5 bg-clip-500 hover:bg-clip-600 text-white font-bold rounded-xl md:rounded-2xl transition-all shadow-lg shadow-clip-500/30 flex items-center justify-center gap-2 text-sm md:text-base"
                    >
                        <Plus size={18} />
                        {t('home.book_now')}
                    </motion.button>

                    <motion.button
                        whileHover={{
                            scale: 1.05,
                            backgroundColor: 'rgba(255,255,255,0.2)',
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onViewAgenda}
                        className="w-full sm:w-auto px-6 py-3 md:px-8 md:py-3.5 bg-white/10 text-white font-medium rounded-xl md:rounded-2xl transition-all backdrop-blur-sm flex items-center justify-center gap-2 border border-white/10 text-sm md:text-base"
                    >
                        <CalendarIcon size={18} />
                        {t('home.view_agenda')}
                    </motion.button>
                </div>
            </div>
        </div>
    );
};

export default HomeHero;
